import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useApiRequest from "../../../../../../utils/hooks/useApiRequest";
import useBasketManager from "../../../../FinalizePage/hooks/useBasketManager";
import usePreBasket from "../../../../Universal/hooks/usePreBasket";
import { format } from "date-fns";
import { Assort } from "../../../../../models/Assort";
import getId from "../../../../../../utils/getId";
import { BasketItem } from "../../../../../models/BasketItem";
import { ADD_TO_BASKET_URL } from "../../../../../api/apiRoutes";
import { TicketAccess } from "../../../../../models/TicketAccess";
import { setAddToBasketMessage } from "../../../../../store/alerts/alertsActions";
import { State } from "../../../../../store";
import { FINALIZE_PAGE } from "../../../../../routes";

export default function useCourtTicketSelection(
  exerciseId: number,
  ticketAccess: TicketAccess,
  ticketLimit: number,
  onTicketsAdded: () => void
) {
  const { post } = useApiRequest();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const saleConfiguration = useSelector((state: State) => state.config.configuration);

  const { preBasket, addItemToBasket, removeItemFromBasket, clearBasket, setItemQuantity } = usePreBasket();
  const { getBasket, basket } = useBasketManager();

  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  const [alert, setAlert] = useState<string>("");
  const [isSavingBasket, setIsSavingBasket] = useState<boolean>(false);

  useEffect(() => {
    setAlert("");
  }, [exerciseId]);

  const formatTimeRange = (startTime: number, endTime: number) => {
    const startFormatted = format(startTime, "HH:mm");
    const endFormatted = format(endTime, "HH:mm");
    return `${startFormatted} - ${endFormatted}`;
  };

  const getQnt = (value: Assort): number => {
    const preBasketQnt = preBasket
      .filter(
        (item) =>
          item.assortIds === getId(value) &&
          item.ticketAccess &&
          item.ticketAccess.length > 0 &&
          item.ticketAccess[0].exerciseIds === ticketAccess?.exerciseIds &&
          item.ticketAccess[0].startDate === ticketAccess?.startDate,
      )
      .reduce((acc: number, item: BasketItem) => acc + item.qnt, 0);

    const basketQnt = basket
      .filter(
        (item) =>
          item.assortIds === getId(value) &&
          item.ticketAccess &&
          item.ticketAccess.length > 0 &&
          item.ticketAccess[0].exerciseIds === ticketAccess?.exerciseIds &&
          item.ticketAccess[0].startDate === ticketAccess?.startDate,
      )
      .reduce((acc: number, item: BasketItem) => acc + item.qnt, 0);

    return preBasketQnt + basketQnt;
  };

  const ticketsInCurrentBasket = () => {
    const preBasketCount = preBasket
      .filter(
        (item) =>
          item.ticketAccess &&
          item.ticketAccess.length > 0 &&
          item.ticketAccess[0].exerciseIds === ticketAccess?.exerciseIds &&
          item.ticketAccess[0].startDate === ticketAccess?.startDate,
      )
      .reduce((acc: number, item: BasketItem) => acc + item.qnt, 0);

    const basketCount = basket
      .filter(
        (item) =>
          item.ticketAccess &&
          item.ticketAccess.length > 0 &&
          item.ticketAccess[0].exerciseIds === ticketAccess?.exerciseIds &&
          item.ticketAccess[0].startDate === ticketAccess?.startDate,
      )
      .reduce((acc: number, item: BasketItem) => acc + item.qnt, 0);

    return preBasketCount + basketCount;
  };

  const getEffectiveMax = (item: Assort) => {
    let effectiveMax = item.maxSale || 99;

    if (ticketLimit || ticketLimit === 0) {
      const currentQnt = getQnt(item);
      const totalTicketsInBaskets = ticketsInCurrentBasket();
      const remainingTickets = ticketLimit - totalTicketsInBaskets + currentQnt;
      effectiveMax = Math.min(effectiveMax, remainingTickets);
    }

    return Math.max(0, effectiveMax);
  };

  const handleQuantityChange = (item: Assort, newValue: number) => {
    if (setItemQuantity) {
      setItemQuantity(item, newValue, ticketAccess);
    }
  };

  const sumBasket = () => preBasket.reduce((acc: number, item: BasketItem) => acc + item.price * item.qnt, 0);

  const addBasketRequest = async () => {
    try {
      const response = [];

      // Wysyłaj requesty sekwencyjnie z małym opóźnieniem
      for (const params of preBasket) {
        const result = await post(ADD_TO_BASKET_URL, params);
        response.push(result);

        // Dodaj małe opóźnienie między requestami (50ms)
        if (preBasket.indexOf(params) < preBasket.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
      }

      const hasErrors = response.some((item: any) => item.isError);
      const errorMessages = response
        .filter((item: any) => item.isError && item.errorTable?.length > 0)
        .map((item: any) => item.errorTable[0]);

      if (hasErrors && errorMessages.length > 0) {
        setAlert(errorMessages[0]);
        return false;
      } else {
        finalizeFunction();
        return true;
      }
    } catch (error) {
      setAlert("Wystąpił błąd podczas dodawania do koszyka");
      return false;
    }
  };

  const finalizeFunction = () => {
    // Wywołaj callback dla odświeżenia danych
    onTicketsAdded();

    // Sprawdź konfigurację popup
    if (saleConfiguration.popupAfterAddTicket === true) {
      dispatch(setAddToBasketMessage(t("dodano_do_koszyka")));
    }

    if (saleConfiguration.popupAfterAddTicket === false) {
      setTimeout(() => {
        navigate(FINALIZE_PAGE);
      }, 100);
    }
  };

  const saveBasket = async () => {
    setIsSavingBasket(true);
    try {
      const success = await addBasketRequest();
      if (success) {
        clearBasket();
        await getBasket();
      }
    } finally {
      setIsSavingBasket(false);
    }
  };

  return {
    formatTimeRange,
    getQnt,
    getEffectiveMax,
    handleQuantityChange,
    addItemToBasket,
    removeItemFromBasket,
    sumBasket,
    saveBasket,
    alert,
    setAlert,
    openTooltip,
    setOpenTooltip,
    preBasket,
    ticketsInCurrentBasket,
    isSavingBasket,
  };
}
